import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import PaginationBar from '../components/PaginationBar';
import {
  Briefcase, MapPin, Clock, CheckCircle2, XCircle, Eye,
  Search, Filter, Loader2, TrendingUp, Calendar, X
} from 'lucide-react';

// Covers both backend canonical values (reviewed, hired) and
// frontend-historic aliases (reviewing, offered)
const PAGE_SIZE = 10;

const STATUS_CONFIG = {
  pending:     { label: 'Applied',        color: 'bg-blue-100 text-blue-700',    icon: Clock },
  reviewing:   { label: 'Under Review',   color: 'bg-amber-100 text-amber-700',  icon: Eye },
  reviewed:    { label: 'Under Review',   color: 'bg-amber-100 text-amber-700',  icon: Eye },
  shortlisted: { label: 'Shortlisted',    color: 'bg-purple-100 text-purple-700',icon: TrendingUp },
  interview:   { label: 'Interview',      color: 'bg-indigo-100 text-indigo-700',icon: Calendar },
  offered:     { label: 'Offer Received', color: 'bg-emerald-100 text-emerald-700',icon: CheckCircle2 },
  hired:       { label: 'Offer Received', color: 'bg-emerald-100 text-emerald-700',icon: CheckCircle2 },
  rejected:    { label: 'Not Selected',   color: 'bg-red-100 text-red-700',      icon: XCircle },
  withdrawn:   { label: 'Withdrawn',      color: 'bg-gray-100 text-gray-500',    icon: X },
};

// Pipeline uses backend canonical values
const PIPELINE_STAGES = ['pending', 'reviewed', 'shortlisted', 'interview', 'hired'];

export default function ApplicationTrackerPage() {
  const { api } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [withdrawing, setWithdrawing] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => { fetchApplications(); }, []);

  // A new search or status filter starts again from the first page.
  useEffect(() => { setPage(1); }, [statusFilter, search]);

  const fetchApplications = async () => {
    try {
      const res = await api.get('/applications/my');
      setApplications(res.data);
    } catch {
      toast.error('Failed to load applications');
    } finally { setLoading(false); }
  };

  const handleWithdraw = async (appId) => {
    if (!window.confirm('Withdraw this application? The employer will no longer be able to review it.')) return;
    setWithdrawing(appId);
    try {
      await api.put(`/applications/${appId}/withdraw`);
      toast.success('Application withdrawn');
      fetchApplications();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to withdraw application');
    } finally { setWithdrawing(null); }
  };

  // Normalise status so reviewing→reviewed, offered→hired for pipeline counts
  const normaliseStatus = (s) => {
    if (s === 'reviewing') return 'reviewed';
    if (s === 'offered')   return 'hired';
    return s;
  };

  const filtered = applications.filter(app => {
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter || normaliseStatus(app.status) === statusFilter;
    const matchesSearch = !search ||
      (app.job_title || '').toLowerCase().includes(search.toLowerCase()) ||
      (app.company_name || '').toLowerCase().includes(search.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // Count using normalised statuses
  const statusCounts = applications.reduce((acc, app) => {
    const s = normaliseStatus(app.status);
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#003366]" />
    </div>
  );

  const canWithdraw = (status) =>
    !['rejected', 'hired', 'offered', 'withdrawn'].includes(status);

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      <div className="bg-[#003366] text-white py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold mb-2">Application Tracker</h1>
          <p className="text-white/70">{applications.length} total application{applications.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Pipeline Overview */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {PIPELINE_STAGES.map(stage => {
            const cfg = STATUS_CONFIG[stage];
            const Icon = cfg.icon;
            return (
              <Card
                key={stage}
                className={`cursor-pointer transition-all hover:shadow-md ${statusFilter === stage ? 'ring-2 ring-[#003366]' : ''}`}
                onClick={() => setStatusFilter(statusFilter === stage ? 'all' : stage)}
              >
                <CardContent className="p-4 text-center">
                  <Icon className="w-6 h-6 mx-auto mb-1 text-[#003366]" />
                  <p className="text-2xl font-bold text-[#003366]">{statusCounts[stage] || 0}</p>
                  <p className="text-xs text-muted-foreground">{cfg.label}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Pipeline bar */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-0">
              {PIPELINE_STAGES.map((stage, i) => {
                const cfg = STATUS_CONFIG[stage];
                const count = statusCounts[stage] || 0;
                return (
                  <React.Fragment key={stage}>
                    <div className={`flex-1 text-center py-2 px-1 rounded ${count > 0 ? cfg.color.split(' ')[0] : 'bg-gray-100'}`}>
                      <p className="text-xs font-medium text-gray-600 truncate">{cfg.label}</p>
                      <p className="font-bold text-gray-800">{count}</p>
                    </div>
                    {i < PIPELINE_STAGES.length - 1 && <div className="text-gray-300 text-lg px-1">›</div>}
                  </React.Fragment>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Rejected / Withdrawn counts */}
        {(statusCounts.rejected > 0 || statusCounts.withdrawn > 0) && (
          <div className="flex gap-3 flex-wrap">
            {statusCounts.rejected > 0 && (
              <button
                onClick={() => setStatusFilter(statusFilter === 'rejected' ? 'all' : 'rejected')}
                className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-colors ${statusFilter === 'rejected' ? 'bg-red-100 border-red-200 text-red-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-red-50'}`}
              >
                <XCircle className="w-4 h-4" /> {statusCounts.rejected} Not selected
              </button>
            )}
            {statusCounts.withdrawn > 0 && (
              <button
                onClick={() => setStatusFilter(statusFilter === 'withdrawn' ? 'all' : 'withdrawn')}
                className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-colors ${statusFilter === 'withdrawn' ? 'bg-gray-100 border-gray-200 text-gray-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
              >
                <X className="w-4 h-4" /> {statusCounts.withdrawn} Withdrawn
              </button>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-3 flex-wrap">
          <div className="relative flex-1 min-w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input className="pl-9" placeholder="Search by job or company..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Applied</SelectItem>
              <SelectItem value="reviewed">Under Review</SelectItem>
              <SelectItem value="shortlisted">Shortlisted</SelectItem>
              <SelectItem value="interview">Interview</SelectItem>
              <SelectItem value="hired">Offer Received</SelectItem>
              <SelectItem value="rejected">Not Selected</SelectItem>
              <SelectItem value="withdrawn">Withdrawn</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* List */}
        {filtered.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">No applications found</h3>
              <p className="text-muted-foreground mb-4">
                {applications.length === 0 ? "You haven't applied to any jobs yet." : "No applications match your filter."}
              </p>
              {applications.length === 0 && (
                <Link to="/jobs"><Button className="bg-[#003366] hover:bg-[#003366]/90">Browse Jobs</Button></Link>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map(app => {
              const cfg = STATUS_CONFIG[app.status] || STATUS_CONFIG.pending;
              const Icon = cfg.icon;
              return (
                <Card key={app.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="font-semibold text-base">{app.job_title || 'Job Application'}</h3>
                          <Badge className={cfg.color}>
                            <Icon className="w-3 h-3 mr-1" />
                            {cfg.label}
                          </Badge>
                        </div>
                        <p className="text-muted-foreground font-medium">{app.company_name}</p>
                        <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
                          {app.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3.5 h-3.5" />{app.location}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            Applied {new Date(app.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                          {app.interview_date && (
                            <span className="flex items-center gap-1 text-indigo-600 font-medium">
                              <Calendar className="w-3.5 h-3.5" />
                              Interview: {new Date(app.interview_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                        {app.cover_letter && (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg border">
                            <p className="text-xs font-medium text-muted-foreground mb-1">Cover Letter</p>
                            <p className="text-sm line-clamp-2">{app.cover_letter}</p>
                          </div>
                        )}
                        {(app.notes || app.employer_notes) && (
                          <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
                            <p className="text-xs font-medium text-blue-700 mb-1">Employer Feedback</p>
                            <p className="text-sm text-blue-800">{app.notes || app.employer_notes}</p>
                          </div>
                        )}
                      </div>
                      <div className="shrink-0 flex flex-col gap-2 items-end">
                        <Link to={`/jobs/${app.job_id}`}>
                          <Button size="sm" variant="outline">View Job</Button>
                        </Link>
                        {canWithdraw(app.status) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            disabled={withdrawing === app.id}
                            onClick={() => handleWithdraw(app.id)}
                          >
                            {withdrawing === app.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5 mr-1" />}
                            Withdraw
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            <PaginationBar page={page} pageSize={PAGE_SIZE} total={filtered.length} onPageChange={setPage} />
          </div>
        )}
      </div>
    </div>
  );
}
